/**
 * Workstations, first test (anvil). Pure and deterministic: time arrives as `nowMs` (real clock, injected by
 * the client like the crop / offline-job hooks), randomness only from `h.random()`.
 * buy -> place on an authored pad -> start a forge job (real-time timer, runs while the player is away) ->
 * collect with a timing minigame. A bandit raid on a WILD pad takes only the bonus, never the base yield.
 * Town pads are safe. Using another player's anvil for a fee is a stub (`useFee`).
 */
import { ANVIL, PADS, STATIONS, type PadDef } from '../data/stations';
import type { SystemHost, WorldSystem } from './types';

export interface Anvil {
  padId: string | null;
  /** Job end in real ms, or null when idle. */
  endsAt: number | null;
  /** Bonus was taken by a raid. */
  raided: boolean;
}
export type CollectGrade = 'perfect' | 'good' | 'miss';

export class StationsSystem implements WorldSystem {
  readonly name = 'stations';
  gold = 0;
  /** Owned anvils (bought, maybe not yet placed). */
  anvils: Anvil[] = [];
  /** Forged items in the pouch. */
  blades = 0;
  /** Fees owed to the owner from other players (stub counter). */
  feesEarned = 0;
  constructor(private readonly h: SystemHost) {}

  private ev(e: Record<string, unknown>): void {
    this.h.emit({ type: 'stations', ...e } as never);
  }
  pad(id: string): PadDef | undefined {
    return PADS.find((p) => p.id === id);
  }
  anvilAt(padId: string): Anvil | undefined {
    return this.anvils.find((a) => a.padId === padId);
  }

  buy(): boolean {
    if (this.gold < ANVIL.price) return false;
    this.gold -= ANVIL.price;
    this.anvils.push({ padId: null, endsAt: null, raided: false });
    this.ev({ phase: 'buy' });
    return true;
  }
  place(padId: string): boolean {
    if (!this.pad(padId) || this.anvilAt(padId)) return false;
    const a = this.anvils.find((x) => x.padId === null);
    if (!a) return false;
    a.padId = padId;
    this.ev({ phase: 'place', padId });
    return true;
  }
  start(padId: string, nowMs: number): boolean {
    const a = this.anvilAt(padId);
    if (!a || a.endsAt !== null) return false;
    a.endsAt = nowMs + STATIONS.jobMs;
    a.raided = false;
    this.ev({ phase: 'start', padId });
    return true;
  }
  /**
   * Catch up after time away (call at boot and every frame with the real clock): a finished wild job may have
   * been raided. The roll is one `h.random()` draw per finished wild job, so it is the same on every machine
   * for the same seed. Returns the pads whose job is done.
   */
  catchUp(nowMs: number): string[] {
    const done: string[] = [];
    for (const a of this.anvils) {
      if (a.padId === null || a.endsAt === null || nowMs < a.endsAt) continue;
      done.push(a.padId);
      if (this.pad(a.padId)!.kind === 'wild' && !a.raided && this.rolled.get(a) === undefined) {
        const hit = this.h.random() < STATIONS.raidChance;
        this.rolled.set(a, hit);
        if (hit) {
          a.raided = true;
          this.ev({ phase: 'raid', padId: a.padId });
        }
      }
    }
    return done;
  }
  private rolled = new WeakMap<Anvil, boolean>();

  remainingMs(padId: string, nowMs: number): number {
    const a = this.anvilAt(padId);
    return a && a.endsAt !== null ? Math.max(0, a.endsAt - nowMs) : 0;
  }
  /** Cursor position 0..1 of the collect minigame at `tMs` since it opened (ping-pong). */
  static cursor(tMs: number): number {
    const p = (tMs / STATIONS.sweepMs) % 2;
    return p <= 1 ? p : 2 - p;
  }
  static grade(cursor: number): CollectGrade {
    const d = Math.abs(cursor - 0.5);
    return d <= STATIONS.perfectZone ? 'perfect' : d <= STATIONS.goodZone ? 'good' : 'miss';
  }
  /** Collect a finished job; `cursor` is where the player stopped the sweep. A miss still yields the base. */
  collect(padId: string, nowMs: number, cursor: number): { grade: CollectGrade; n: number } | null {
    this.catchUp(nowMs);
    const a = this.anvilAt(padId);
    if (!a || a.endsAt === null || nowMs < a.endsAt) return null;
    const wild = this.pad(padId)!.kind === 'wild';
    const grade = StationsSystem.grade(cursor);
    let n = STATIONS.baseYield;
    if (wild && !a.raided) n += STATIONS.wildBonus;
    if (grade === 'perfect') n += STATIONS.perfectBonus;
    this.blades += n;
    a.endsAt = null;
    a.raided = false;
    this.ev({ phase: 'collect', padId, grade, n });
    return { grade, n };
  }
  /** Stub: another player uses your anvil for the fee. */
  useByOther(padId: string): boolean {
    if (!this.anvilAt(padId)) return false;
    this.feesEarned += STATIONS.useFee;
    this.gold += STATIONS.useFee;
    return true;
  }
}
